import React from 'react';
import { Card } from '../../common/Card';
import {
  extractFinancialYearPeriods,
  extractCalendarMonths,
  monthLabel,
  getCurrentIndianFinancialYearPeriod,
  type QuarterKey,
} from '../../../utils/regionalClientPerformanceUtils';
import type { RegionalPerformanceRow } from '../../../utils/regionalClientPerformanceUtils';

export type TimePeriodFiltersVariant = 'card' | 'toolbar';

export interface TimePeriodFiltersProps {
  data: RegionalPerformanceRow[];
  selectedYear: string;
  selectedQuarter: QuarterKey;
  selectedMonth: string;
  onYear: (v: string) => void;
  onQuarter: (v: QuarterKey) => void;
  onMonth: (v: string) => void;
  selectedRegion: string;
  regionLabel: string;
  recordCount: number;
  /** When set with `onRegionChange`, shows a region filter (e.g. entity performance page). */
  regionOptions?: string[];
  onRegionChange?: (region: string) => void;
  /** `toolbar` = compact row for PageHeader (top right). `card` = full block below header. */
  variant?: TimePeriodFiltersVariant;
}

const QUARTERS: { key: QuarterKey; label: string; active: string }[] = [
  { key: 'q1', label: 'Q1', active: 'bg-blue-600 text-white' },
  { key: 'q2', label: 'Q2', active: 'bg-emerald-600 text-white' },
  { key: 'q3', label: 'Q3', active: 'bg-amber-500 text-white' },
  { key: 'q4', label: 'Q4', active: 'bg-violet-600 text-white' },
];

export function TimePeriodFilters({
  data,
  selectedYear,
  selectedQuarter,
  selectedMonth,
  onYear,
  onQuarter,
  onMonth,
  selectedRegion,
  regionLabel,
  recordCount,
  regionOptions,
  onRegionChange,
  variant = 'card',
}: TimePeriodFiltersProps) {
  let periods = extractFinancialYearPeriods(data);
  const currentFY = getCurrentIndianFinancialYearPeriod();
  if (
    selectedYear !== 'all' &&
    selectedYear &&
    !periods.includes(selectedYear)
  ) {
    periods = [selectedYear, ...periods].sort((a, b) => b.localeCompare(a));
  } else if (periods.length === 0 && currentFY) {
    periods = [currentFY];
  }

  const months = extractCalendarMonths(data);

  const fyRangeHint =
    periods.length > 0
      ? `FY ${periods[periods.length - 1]} to FY ${periods[0]}`
      : '';

  const title =
    selectedRegion !== 'all'
      ? `${regionLabel} — period (Indian FY)`
      : `Time period (Indian FY)${selectedYear === 'all' && periods.length > 0 ? ` (${fyRangeHint})` : ''}`;

  const periodSummary = [
    selectedRegion !== 'all' ? `${regionLabel} · ` : '',
    selectedYear === 'all'
      ? periods.length > 0
        ? fyRangeHint
        : 'All FY'
      : `FY ${selectedYear}`,
    selectedYear !== 'all' && selectedQuarter !== 'all'
      ? ` · ${selectedQuarter.toUpperCase()}`
      : '',
    selectedYear !== 'all' &&
    selectedQuarter === 'all' &&
    selectedMonth !== 'all'
      ? ` · ${monthLabel(parseInt(selectedMonth, 10))}`
      : '',
    selectedYear !== 'all' &&
    selectedQuarter === 'all' &&
    selectedMonth === 'all'
      ? ' · All months'
      : '',
    ` · ${recordCount} record${recordCount !== 1 ? 's' : ''}`,
  ].join('');

  const showRegionFilter =
    regionOptions != null &&
    regionOptions.length > 0 &&
    typeof onRegionChange === 'function';

  const controls = (
    <>
      {showRegionFilter && (
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Region
          </span>
          <select
            value={selectedRegion}
            onChange={e => onRegionChange(e.target.value)}
            className="text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[6.5rem] max-w-[10rem] truncate"
            title={selectedRegion === 'all' ? 'All regions' : regionLabel}
          >
            <option value="all">All regions</option>
            {(regionOptions ?? []).map(r => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          FY
        </span>
        <select
          value={selectedYear}
          onChange={e => {
            onYear(e.target.value);
            if (e.target.value === 'all') {
              onQuarter('all');
              onMonth('all');
            }
          }}
          className="text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[5.5rem]"
        >
          <option value="all">All FY</option>
          {periods.map(p => (
            <option key={p} value={p}>
              FY {p}
            </option>
          ))}
        </select>
      </div>
      {selectedYear !== 'all' && (
        <div className="flex items-center gap-1 flex-wrap shrink-0">
          {QUARTERS.map(({ key, label, active }) => (
            <button
              key={key}
              type="button"
              onClick={e => {
                e.preventDefault();
                onQuarter(key);
                onMonth('all');
              }}
              className={`px-2 py-0.5 rounded text-[11px] font-semibold transition-colors ${
                selectedQuarter === key
                  ? active
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            onClick={e => {
              e.preventDefault();
              onQuarter('all');
              onMonth('all');
            }}
            className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
              selectedQuarter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-600'
            }`}
          >
            All
          </button>
        </div>
      )}
      {selectedYear !== 'all' && selectedQuarter === 'all' && (
        <select
          value={selectedMonth}
          onChange={e => {
            onMonth(e.target.value);
            onQuarter('all');
          }}
          className="text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[7rem] shrink-0"
        >
          <option value="all">All months</option>
          {months.map(m => (
            <option key={m} value={String(m)}>
              {monthLabel(m)}
            </option>
          ))}
        </select>
      )}
    </>
  );

  if (variant === 'toolbar') {
    return (
      <div
        className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1.5 w-full min-w-0"
        title={periodSummary}
      >
        {controls}
      </div>
    );
  }

  return (
    <Card padding="sm" className="shadow-md">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[12px] font-semibold text-slate-800">{title}</h3>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end">
          {controls}
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-2">{periodSummary}</p>
    </Card>
  );
}
